import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { CartService } from "../../services/cart";
import { Cart, CartModal, DetailedCartItem } from "./CartContext.api.types";
import { cartReducer } from "./CartContext.reducer";
import { AddToCartItem, LastProductAdded, State } from "./CartContext.types";

const initialState = {
  cartItems: [],
  cart: null,
  cartSize: 0,
  cartModalData: [],
  status: "pending",
  lastProductAdded: null,
};

const CartStateContext = React.createContext<
  | {
      cartItems: AddToCartItem[];
      cart: Cart | null;
      cartSize: number;
      addToCart: (item: AddToCartItem) => void;
      removeFromCart: (variationId: string) => void;
      updateItemQuantity: (variationId: string, quantity: number) => void;
      clearCart: () => void;
      getCartItem: (variationId: string) => AddToCartItem | null;
      isLoading: () => boolean;
      isLoadingCart: boolean; // v2 of isLoading, sometimes doesn't work as expected
      cartModalData: CartModal | [];
      hasGoldVariations: boolean;
      hasSilverVariations: boolean;
      getGoldVariations: () => DetailedCartItem[] | [];
      getSilverVariations: () => DetailedCartItem[];
      lastProductAdded: LastProductAdded | null;
      setLastProductAdded: (lastProductAdded: LastProductAdded) => void;
    }
  | undefined
>(undefined);

type CartProviderProps = { children: React.ReactNode };

const getInitialState = (): State => {
  const cartItems = CartService.get();

  return {
    ...initialState,
    cartItems: cartItems,
    cartSize: 0,
    status: "pending",
    cartModalData: [],
  };
};

const CartProvider: FunctionComponent<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState());

  const updateCart = async (items: AddToCartItem[]) => {
    CartService.store({ items: items });

    try {
      const cart = await CartService.create({ items: items });
      if (cart)
        dispatch({
          type: "UPDATE_CART",
          payload: { cart: cart },
        });
    } catch (e) {
      console.log("[DEBUG/CartContext] - Error building cart - ", e);
    }
  };

  const getModalData = async (items: AddToCartItem[]) => {
    try {
      const cartModalData: CartModal | [] = await CartService.getModalData({
        items: items,
      });
      if (cartModalData)
        dispatch({ type: "GET_CART_MODAL_DATA", payload: { cartModalData } });
    } catch (e) {
      console.log("[DEBUG/CartContext] - Error building cart modal - ", e);
    }
  };

  const handleAddToCart = async (item: AddToCartItem) => {
    dispatch({ type: "ADD_TO_CART", payload: { item } });
  };

  const handleLastProductAdded = async (lastProductAdded: LastProductAdded) => {
    dispatch({ type: "SET_LAST_PRODUCT", payload: { lastProductAdded } });
  };

  const handleRemoveFromCart = async (variationId: string) => {
    dispatch({ type: "REMOVE_FROM_CART", payload: { variationId } });
  };

  const handleClearCart = async () => {
    dispatch({ type: "CLEAR_CART" });
  };

  const handleUpdateItemQuantity = async (
    variationId: string,
    quantity: number
  ) => {
    dispatch({
      type: "UPDATE_ITEM_QUANTITY",
      payload: { variationId, quantity },
    });
  };

  const handleGetCartItem = (variationId: string) => {
    const cartItem = state.cartItems.find(
      (cartItem) => cartItem.variationId === variationId
    );
    if (cartItem) return cartItem;
    return null;
  };

  useEffect(() => {
    updateCart(state.cartItems);
    getModalData(state.cartItems);
  }, [state.cartItems]);

  const value = {
    cart: state.cart,
    cartItems: state.cartItems,
    cartSize: state.cartSize ?? 0,
    cartModalData: state.cartModalData,
    lastProductAdded: state.lastProductAdded,
    isLoadingCart: state.status === "pending",
    addToCart: handleAddToCart,
    setLastProductAdded: handleLastProductAdded,
    removeFromCart: handleRemoveFromCart,
    updateItemQuantity: handleUpdateItemQuantity,
    clearCart: handleClearCart,
    getCartItem: handleGetCartItem,
    isLoading: () => {
      return state.status === "pending";
    },
    hasGoldVariations:
      Number(state.cart?.detailed?.gold?.variations?.length) > 0,
    hasSilverVariations:
      Number(state.cart?.detailed?.silver?.variations?.length) > 0,
    getGoldVariations: () => {
      if (state.cart && state.cart?.detailed?.gold?.variations?.length > 0)
        return state.cart.detailed.gold.variations;
      return [];
    },
    getSilverVariations: () => {
      if (state.cart && state?.cart?.detailed?.silver?.variations?.length > 0)
        return state.cart.detailed.silver.variations;
      return [];
    },
  };

  return (
    <CartStateContext.Provider value={value}>
      {children}
    </CartStateContext.Provider>
  );
};

const useCart = () => {
  const context = useContext(CartStateContext);
  if (context === undefined) {
    throw new Error("useCount must be used within a CartProvider");
  }
  return context;
};

export { CartProvider, useCart };
